import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Link,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useNavigate } from 'react-router-dom';
import default_theme from 'assets/themes/themes';

const LAPSourceModal = ({ open, onClose, source }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      // Reset loading state when modal opens
      setLoading(false);
    }
  }, [open]);

  const handleClose = () => {
    onClose();
  };

  const handleViewFullDetails = () => {
    // LAP sources use UUID/id or title, not entry_id
    const lapId = source?.id || 
                  source?._fullMetadata?.id || 
                  source?.catalog_id || 
                  source?.title;
    
    if (lapId) {
      // Extract dynamo code from source - check multiple locations
      // Try direct field first, then metadata, then extract from title pattern (e.g., "D1-LAP-S1" -> "D1")
      let dynamoCode = source?.dynamo_code || 
                       source?._fullMetadata?.dynamo_code ||
                       null;
      
      // Helper function to extract dynamo code from a string (title pattern)
      const extractDynamoFromString = (str) => {
        if (!str) return null;
        const match = str.toString().match(/^([A-Z]\d+)-LAP-/i);
        return match ? match[1] : null;
      };
      
      // If not found, try to extract from title in various locations
      if (!dynamoCode) {
        dynamoCode = extractDynamoFromString(source?.title) ||
                     extractDynamoFromString(source?._fullMetadata?.title) ||
                     extractDynamoFromString(source?.catalog_id) ||
                     extractDynamoFromString(source?._fullMetadata?.catalog_id);
      }
      
      // Debug logging (can be removed in production)
      console.log('LAPSourceModal - Navigation:', {
        lapId,
        dynamoCode,
        sourceTitle: source?.title,
        metadataTitle: source?._fullMetadata?.title,
        catalogId: source?.catalog_id,
        source: source
      });
      
      // Navigate to LAP detail page with dynamo code if available
      if (dynamoCode) {
        navigate(`/solutions/rural-development/lap-dynamo/${dynamoCode}/lap-catalog/${lapId}`);
      } else {
        // Fallback: navigate without dynamo code (will go to catalog list)
        navigate(`/solutions/rural-development/lap-catalog/${lapId}`);
      }
      handleClose();
    }
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle 
        sx={{ 
          m: 0, 
          p: 2, 
          bgcolor: default_theme.palette.primary.white, 
          borderBottom: `2px solid ${default_theme.palette.other.main}` 
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography 
            variant="h5" 
            component="div" 
            sx={{ 
              fontWeight: 700, 
              color: default_theme.palette.other.textDark, 
              pr: 2 
            }}
          >
            {source?.solution_title || source?.title || 'LAP Details'}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              color: default_theme.palette.other.main,
              '&:hover': { bgcolor: default_theme.palette.other.tealHover }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: default_theme.palette.primary.white, p: 3, maxHeight: 'calc(90vh - 120px)', overflow: 'auto' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: default_theme.palette.other.main }} />
          </Box>
        )}

        {!loading && source && (
            <Box>
            {/* Header Info */}
            <Box sx={{ mb: 3, p: 4, bgcolor: default_theme.palette.primary.main, borderRadius: 2, boxShadow: 2 }}>
              {/* Location */}
              {source.location && (
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationOnIcon sx={{ color: default_theme.palette.other.main, fontSize: '1.2rem' }} />
                  <Typography variant="body2" sx={{ color: default_theme.palette.other.textSecondary }}>
                    {source.location}
                  </Typography>
                </Box>
              )}

              {/* Brief Description Section */}
              <Box sx={{ mb: 3, minHeight: '200px' }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: default_theme.palette.other.main, 
                    fontWeight: 600, 
                    mb: 2 
                  }}
                >
                  Brief Description
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    whiteSpace: 'pre-wrap', 
                    lineHeight: 1.8,
                    color: default_theme.palette.other.textDark,
                    // maxHeight: 'none',
                    overflow: 'visible'
                  }}
                >
                  {source.brief_description || 
                   source.objectives_of_the_solution || 
                   source.related_challenges ||
                   source.geography_and_territorial_context ||
                   source.summary || 
                   source.aim || 
                   'No description available.'}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {/* View Full Details Button */}
        {!loading && (source?.id || source?._fullMetadata?.id || source?.catalog_id || source?.title) && (
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={handleViewFullDetails}
              sx={{
                bgcolor: default_theme.palette.other.main,
                color: default_theme.palette.primary.main,
                fontWeight: 600,
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '16px',
                '&:hover': {
                  bgcolor: default_theme.palette.other.tealDark,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 6px 20px ${default_theme.palette.other.tealHover}`
                },
                transition: 'all 0.3s ease'
              }}
            >
              View Full Details
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LAPSourceModal;